import { Component, ElementRef, forwardRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import {
  CustomFieldCollectionMemberMergeDto,
  CustomFieldCompositionDto,
  CustomFieldItemDto,
  CustomFieldService,
} from '@artemis-software/wr-api';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import Sortable from 'sortablejs';
import { NbAdjustment, NbPopoverDirective, NbPosition } from '@nebular/theme';
import { catalogTenantId } from '@/utils/admin-utils';
import { displayUnitSymbol } from '@shared/pipes/unit-symbols';
import { isTriggerCandidate, triggerValueOptions } from '@shared/custom-form/custom-field-visibility';

export interface TriggerCandidate {
  fieldId: string;
  name: string;
  options: { value: string; label: string }[];
}

export type ResolvedMember = CustomFieldCollectionMemberMergeDto & {
  field: CustomFieldItemDto;
  /** Display data for the configured visibility rule; undefined when the member has none. */
  rule?: { triggerName: string; valueLabel: string; stale: boolean };
  /** Members of this collection that may serve as this member's trigger (cycle-free). */
  candidates: TriggerCandidate[];
  /** Value options of the currently configured trigger; empty when none is configured. */
  triggerOptions: { value: string; label: string }[];
};

const OVERLAY_PANE_CLASS = 'cdk-overlay-pane';

@Component({
  standalone: false,
  selector: 'wr-custom-field-layout-editor',
  templateUrl: './custom-field-layout-editor.component.html',
  styleUrls: ['./custom-field-layout-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomFieldLayoutEditorComponent),
      multi: true,
    },
  ],
})
export class CustomFieldLayoutEditorComponent extends AbstractFormControl<CustomFieldCollectionMemberMergeDto[]> implements OnInit, OnDestroy {

  @ViewChild('fieldsPicker') fieldsPicker?: NbPopoverDirective;

  readonly NbPosition = NbPosition;
  readonly NbAdjustment = NbAdjustment;

  readonly destroy$ = new Subject<void>();
  readonly searchText$ = new BehaviorSubject('');
  readonly currentFolder$ = new BehaviorSubject('');

  resolvedMembers$?: Observable<ResolvedMember[]>;
  filteredFields$?: Observable<CustomFieldItemDto[]>;
  foldersWithCount$?: Observable<{ name: string; fullPath: string; count: number }[]>;

  readonly colSpanOptions = [
    { value: 3, label: '¼', tooltip: '25% Breite' },
    { value: 4, label: '⅓', tooltip: '33% Breite' },
    { value: 6, label: '½', tooltip: '50% Breite' },
    { value: 12, label: '↔', tooltip: 'Volle Breite' },
  ];

  get sortableOptions(): Sortable.Options {
    return { disabled: this.isDisabled, handle: '.drag-handle' };
  }

  private openVisibilityPopover?: NbPopoverDirective;
  private visibilityToggleClick?: Event;
  private readonly currentTenantId = catalogTenantId();
  private readonly customFieldService = inject(CustomFieldService);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  ngOnInit(): void {
    const allFields$ = this.customFieldService.findAll({
      tenantId: this.currentTenantId(),
      sort: { sort: 'name', order: 'ASC' },
    }).pipe(shareReplay(1), takeUntil(this.destroy$));

    this.resolvedMembers$ = combineLatest([allFields$, this.value$]).pipe(
      debounceTime(50),
      map(([allFields, value]) => {
        const base = (value ?? []).map(memberDto => {
          const field = allFields.find(f => f.id === memberDto.fieldId);
          return { ...memberDto, field: field!, colSpan: memberDto.colSpan ?? 12 };
        }).filter(m => !!m.field);
        return base.map(member => this.decorateWithRule(member, base));
      }),
      takeUntil(this.destroy$),
      shareReplay(1),
    );

    const searchTrigger$ = combineLatest([
      this.searchText$.pipe(debounceTime(300), distinctUntilChanged()),
      this.currentFolder$,
    ]).pipe(shareReplay(1));

    this.filteredFields$ = searchTrigger$.pipe(
      switchMap(([text, folder]) =>
        this.customFieldService.findAll({
          text: text || undefined,
          tenantId: this.currentTenantId(),
          category: !text ? folder : undefined,
          sort: { sort: 'name', order: 'ASC' },
        }),
      ),
      map(fields => {
        const selectedIds = new Set((this.value ?? []).map(m => m.fieldId).filter(Boolean) as string[]);
        return fields.filter(f => !selectedIds.has(f.id));
      }),
      shareReplay(1),
      takeUntil(this.destroy$),
    );

    this.foldersWithCount$ = combineLatest([
      searchTrigger$.pipe(
        switchMap(([text, folder]) => {
          if (text) return of([]);
          return this.customFieldService.getFolders(folder);
        }),
      ),
      this.currentFolder$,
      allFields$,
    ]).pipe(
      map(([folderNames, currentFolder, allFields]) =>
        folderNames.map(name => {
          const fullPath = currentFolder ? `${currentFolder}${name}/` : `${name}/`;
          const count = allFields.filter(f => f.category.startsWith(fullPath)).length;
          return { name, fullPath, count };
        }),
      ),
      shareReplay(1),
      takeUntil(this.destroy$),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  onSort(): void {
    this.value = [...(this.value ?? [])];
  }

  setColSpan(fieldId: string | undefined, colSpan: number): void {
    this.value = (this.value ?? []).map(m =>
      m.fieldId === fieldId ? { ...m, colSpan } : m,
    );
  }

  hasRequiredMember(members: ResolvedMember[]): boolean {
    return members.some(m => !!m.required);
  }

  toggleRequired(fieldId: string | undefined): void {
    this.value = (this.value ?? []).map(m =>
      m.fieldId === fieldId ? { ...m, required: !m.required } : m,
    );
  }

  hasVisibilityRule(members: ResolvedMember[]): boolean {
    return members.some(m => !!m.visibleIfFieldId);
  }

  /**
   * The rule popover holds two `nb-select`s, whose option lists render in their own CDK overlay.
   * Nebular's click trigger only treats the popover container itself as "inside", so picking an
   * option counts as an outside click and closes the popover before the value row is ever seen.
   * Hence `noop` plus a hand-rolled open/close that ignores clicks landing in any overlay other
   * than the one the editor itself may sit in.
   */
  toggleVisibilityPopover(event: Event, popover: NbPopoverDirective): void {
    this.visibilityToggleClick = event;
    const reopen = this.openVisibilityPopover !== popover;
    this.closeVisibilityPopover();
    if (!reopen) return;
    popover.show();
    this.openVisibilityPopover = popover;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.openVisibilityPopover) return;
    if (event === this.visibilityToggleClick) return;
    if (this.landedInForeignOverlay(event)) return;
    this.closeVisibilityPopover();
  }

  private landedInForeignOverlay(event: Event): boolean {
    return event.composedPath().some(target =>
      target instanceof Element
      && target.classList.contains(OVERLAY_PANE_CLASS)
      && !target.contains(this.hostElement));
  }

  private closeVisibilityPopover(): void {
    this.openVisibilityPopover?.hide();
    this.openVisibilityPopover = undefined;
  }

  /**
   * Trigger and value are set together — a rule with only one half is rejected by the backend and
   * takes the whole merge (title, ranking, colSpan) down with it. `isTriggerCandidate` guarantees
   * at least one option exists, so the default is always a valid rule the user can then adjust.
   */
  setVisibilityTrigger(member: ResolvedMember, triggerFieldId: string): void {
    const candidate = member.candidates.find(c => c.fieldId === triggerFieldId);
    this.value = (this.value ?? []).map(m =>
      m.fieldId === member.fieldId
        ? { ...m, visibleIfFieldId: triggerFieldId, visibleIfValue: candidate?.options[0]?.value }
        : m,
    );
  }

  setVisibilityValue(fieldId: string | undefined, triggerValue: string): void {
    this.value = (this.value ?? []).map(m =>
      m.fieldId === fieldId ? { ...m, visibleIfValue: triggerValue } : m,
    );
  }

  clearVisibilityRule(fieldId: string | undefined): void {
    this.value = (this.value ?? []).map(m =>
      m.fieldId === fieldId ? { ...m, visibleIfFieldId: undefined, visibleIfValue: undefined } : m,
    );
  }

  /**
   * Removing a field must also drop every rule pointing at it — otherwise the collection would be
   * saved with a trigger that is no longer a member, which the backend rejects.
   */
  remove(fieldId: string | undefined): void {
    this.value = (this.value ?? [])
      .filter(m => m.fieldId !== fieldId)
      .map(m => (m.visibleIfFieldId === fieldId
        ? { ...m, visibleIfFieldId: undefined, visibleIfValue: undefined }
        : m));
  }

  private decorateWithRule(
    member: CustomFieldCollectionMemberMergeDto & { field: CustomFieldItemDto },
    all: (CustomFieldCollectionMemberMergeDto & { field: CustomFieldItemDto })[],
  ): ResolvedMember {
    const candidates: TriggerCandidate[] = all
      .filter(candidate =>
        candidate.fieldId !== member.fieldId
        && isTriggerCandidate(candidate.field)
        && !this.dependsOn(all, candidate.fieldId, member.fieldId))
      .map(candidate => ({
        fieldId: candidate.fieldId!,
        name: candidate.field.name,
        options: triggerValueOptions(candidate.field),
      }));

    const trigger = all.find(m => m.fieldId === member.visibleIfFieldId);
    const triggerOptions = trigger ? triggerValueOptions(trigger.field) : [];

    let rule: ResolvedMember['rule'];
    if (member.visibleIfFieldId) {
      const matched = triggerOptions.find(option => option.value === member.visibleIfValue);
      rule = {
        triggerName: trigger?.field.name ?? 'Unbekanntes Feld',
        valueLabel: matched?.label ?? member.visibleIfValue ?? '',
        // Only a value that WAS set and no longer resolves is stale. A rule still being configured
        // has no value yet and must not be flagged as broken.
        stale: !trigger || (!!member.visibleIfValue && !matched),
      };
    }

    return { ...member, candidates, triggerOptions, rule };
  }

  /** True when `fieldId` depends on `targetFieldId` directly or through a chain of rules. */
  private dependsOn(
    all: CustomFieldCollectionMemberMergeDto[],
    fieldId: string | undefined,
    targetFieldId: string | undefined,
  ): boolean {
    const byField = new Map(all.map(m => [m.fieldId, m]));
    const seen = new Set<string>();
    let current = byField.get(fieldId)?.visibleIfFieldId;
    while (current) {
      if (current === targetFieldId) return true;
      if (seen.has(current)) return false;
      seen.add(current);
      current = byField.get(current)?.visibleIfFieldId;
    }
    return false;
  }

  selectField(field: CustomFieldItemDto): void {
    this.value = [...(this.value ?? []), { fieldId: field.id, required: false, colSpan: 12 }];
    this.fieldsPicker?.hide();
    this.searchText$.next('');
    this.currentFolder$.next('');
  }

  navigateToFolder(event: Event, fullPath: string): void {
    event.stopPropagation();
    this.currentFolder$.next(fullPath);
    this.searchText$.next('');
  }

  navigateToParent(event: Event): void {
    event.stopPropagation();
    const folder = this.currentFolder$.value;
    if (!folder) return;
    const parts = folder.replace(/\/$/, '').split('/');
    parts.pop();
    this.currentFolder$.next(parts.length ? parts.join('/') + '/' : '');
  }

  onSearchInput(event: Event): void {
    this.searchText$.next((event.target as HTMLInputElement).value);
  }

  folderDisplayName(path: string): string {
    return path.replace(/\/$/, '').split('/').pop() ?? path;
  }

  categoryLabel(category: string): string {
    if (!category) return 'Allgemein';
    return category.replace(/\//g, ' › ').replace(/ › $/, '');
  }

  displayFieldType(valueType: CustomFieldCompositionDto.ValueTypeEnum): string {
    switch (valueType) {
      case 'Boolean': return 'Ja/Nein';
      case 'Integer': return 'Zahl';
      case 'Double': return 'Dezimal';
      case 'String': return 'Text';
      case 'Longtext': return 'Langtext';
      case 'LongtextWithPresets': return 'Langtext+';
      case 'MultipleChoice': return 'Auswahl';
      case 'LocalDate': return 'Datum';
      case 'LocalDateTime': return 'Datum & Zeit';
      case 'Attachment': return 'Datei';
      case 'Table': return 'Tabelle';
      default: return valueType;
    }
  }

  readonly displayUnit = displayUnitSymbol;

  widthPercent(colSpan: number): string {
    return `${Math.round(colSpan / 12 * 100)}%`;
  }
}
